import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'

import { DATE_FORMAT } from 'common/constants/dateFormatConstants'
import { DateService } from 'common/services/dateService'
import dayjs, { type Dayjs } from 'dayjs'
import {
  SCHEDULE_TAGS,
  scheduleApi,
  useFetchScheduleDayQuery,
  useFetchScheduleMonthQuery,
} from 'features/Schedule/api/schedule.api'
import {
  scheduleGridQueryOptions,
  scheduleProfileQueryOptions,
  scheduleQueryOptions,
} from 'features/Schedule/api/scheduleQueryOptions'
import type { ScheduleMonth } from 'features/Schedule/api/schedule.types'
import { PathUtils } from 'routes/routes.utils'

import { Col, Checkbox, Row } from 'antd'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'

import { Icon } from '@iconify/react'
import { useAuth } from 'app/providers/auth/AuthProvider'
import { useAppDispatch } from 'common/hooks/redux'
import classNames from 'classnames'
import { Button } from 'common/components/Button/Button'
import Popup from 'common/components/Popup'
import { Select } from 'common/components/Select/Select'
import { SelectCheckbox } from 'common/components/Select-Checkbox/select-checkbox'
import Show from 'common/components/Show2/Show'
import { BUTTON_MODIFIER } from 'common/constants/buttonConstants'
import { LOCALE_STORAGE_KEYS, SESSION_STORAGE_KEYS } from 'common/constants/localeStorageConstants'
import { useTimezone } from 'common/hooks/useTimezone'
import {
  DEFAULT_SCHEDULE_ZOOM_INTERVAL,
  getNextZoomInInterval,
  getNextZoomOutInterval,
  type ScheduleZoomInterval,
} from 'features/Home/Book/Booking/Schedule/PatientSchedule/Calendar/ZoomControls/scheduleZoomIntervals'
import { useMultipleChannels } from 'features/Schedule/pusher/MultiplePusherProvider'
import { useLocalStorage, useSessionStorage } from 'react-use'
import { ScheduleDayView } from './components/ScheduleDayView/ScheduleDayView'
import { ScheduleLoading } from './components/ScheduleLoading/ScheduleLoading'
import { ScheduleWeekView } from './components/ScheduleWeekView/ScheduleWeekView'
import styles from './schedule.module.scss'
import ScheduleHeaderCalendar from 'common/components/ScheduleHeaderCalendar/ScheduleHeaderCalendar'
import { useFetchProfileQuery } from 'features/SettingsOld/state/api/settingsApi'
import { INFO_CONSTANTS } from 'features/Home/constants/infoConstants'

const { preserveDateInTimezone } = DateService

dayjs.extend(isSameOrAfter)
dayjs.extend(customParseFormat)

export enum EScheduleView {
  DAY = 'day',
  WEEK = 'week',
}

const emptyOption = {
  name: 'All',
  code: '-1',
}

const MAX_ACCUMULATED_SCHEDULE_DAYS = 400

const SchedulePage = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { setBookingIds } = useMultipleChannels()

  const { siteId, day } = useParams()
  const [calendarMonthDate, setCalendarMonthDate] = useState<string | null>(null)
  const { timeZone, isFetchingSites } = useTimezone()

  const viewDate = useMemo(() => {
    if (!timeZone) return null

    const source = day && dayjs(day).isValid() ? day : dayjs()
    return preserveDateInTimezone(source, timeZone).format(DATE_FORMAT.PATIENT_TRACKER_DATE)
  }, [day, timeZone])

  const { data: profile } = useFetchProfileQuery(null, scheduleProfileQueryOptions)

  const [accumulatedScheduleData, setAccumulatedScheduleData] = useState<ScheduleMonth>({})

  const { userId, hasEmrEnabled } = useAuth()

  const [currentView, setCurrentView] = useLocalStorage<EScheduleView>(
    LOCALE_STORAGE_KEYS.CURRENT_VIEW,
    EScheduleView.DAY,
  )

  const isDayView = currentView === EScheduleView.DAY

  const [selectedConsultationStaffId, setSelectedConsultationStaffId] = useLocalStorage<
    string | null
  >(LOCALE_STORAGE_KEYS.SELECTED_CONSULTATION_STAFF, null)

  const [showCanceled, setShowCanceled] = useSessionStorage<boolean>(
    SESSION_STORAGE_KEYS.SHOW_CANCELED_SCHEDULES,
    true,
  )

  const [selectedRoomIds, setSelectedRoomIds] = useSessionStorage<string[] | null>(
    SESSION_STORAGE_KEYS.SELECTED_SCHEDULE_ROOMS,
    null,
  )

  const [zoomInterval, setZoomInterval] = useState<ScheduleZoomInterval>(
    DEFAULT_SCHEDULE_ZOOM_INTERVAL,
  )

  const handleZoomIn = useCallback(() => {
    setZoomInterval((level) => getNextZoomInInterval(level))
  }, [])

  const handleZoomOut = useCallback(() => {
    setZoomInterval((level) => getNextZoomOutInterval(level))
  }, [])

  const scheduleMonthQueryArgs = useMemo(() => {
    if (!siteId || !timeZone) return null

    const date = preserveDateInTimezone(calendarMonthDate ?? day ?? dayjs(), timeZone)

    return {
      siteId,
      startDate: date.startOf('month').add(-1, 'month').format(DATE_FORMAT.PATIENT_TRACKER_DATE),
      endDate: date.add(6, 'month').endOf('month').format(DATE_FORMAT.PATIENT_TRACKER_DATE),
    }
  }, [siteId, timeZone, calendarMonthDate, day])

  const {
    data: scheduleMonth,
    isLoading: isLoadingMonth,
    isFetching: isFetchingScheduleMonth,
  } = useFetchScheduleMonthQuery(scheduleMonthQueryArgs!, {
    skip: !scheduleMonthQueryArgs,
    ...scheduleQueryOptions,
  })

  const isPageLoading = isFetchingSites || !viewDate || isLoadingMonth

  const { data: scheduleDay } = useFetchScheduleDayQuery(
    { siteId, date: viewDate! },
    {
      skip: !isDayView || !siteId || !viewDate,
      ...scheduleGridQueryOptions,
    },
  )

  const scheduleRooms = useMemo(() => scheduleDay?.rooms ?? [], [scheduleDay?.rooms])

  const roomFilterOptions = useMemo(
    () =>
      [...scheduleRooms]
        .sort((a, b) => {
          if (a.is_waiting_room && !b.is_waiting_room) return -1
          if (!a.is_waiting_room && b.is_waiting_room) return 1
          return (a.order ?? 0) - (b.order ?? 0)
        })
        .map((room) => ({
          label: room.short_name || room.label,
          value: room.value,
        })),
    [scheduleRooms],
  )

  const doctorsUsers = useMemo(
    () => profile?.users?.filter((user) => user.is_doctor || user.is_provider) ?? [],
    [profile?.users],
  )

  useEffect(() => {
    if (!scheduleMonth) return

    setAccumulatedScheduleData((prevData) => {
      const merged: ScheduleMonth = { ...prevData, ...scheduleMonth }
      const keys = Object.keys(merged).sort()

      if (keys.length <= MAX_ACCUMULATED_SCHEDULE_DAYS) {
        return merged
      }

      // keep the days closest to the freshly fetched window so it always survives the trim
      const anchor = Object.keys(scheduleMonth).sort()[0] ?? keys[0]
      const anchorTime = dayjs(anchor).valueOf()
      const keepKeys = new Set(
        [...keys]
          .sort(
            (a, b) =>
              Math.abs(dayjs(a).valueOf() - anchorTime) - Math.abs(dayjs(b).valueOf() - anchorTime),
          )
          .slice(0, MAX_ACCUMULATED_SCHEDULE_DAYS),
      )
      return Object.fromEntries(Object.entries(merged).filter(([key]) => keepKeys.has(key)))
    })
  }, [scheduleMonth])

  const allDays = useMemo(() => {
    if (!accumulatedScheduleData || Object.keys(accumulatedScheduleData).length === 0) return []
    return Object.keys(accumulatedScheduleData).sort()
  }, [accumulatedScheduleData])

  const availableDays = useMemo(() => {
    return allDays.filter((key) => accumulatedScheduleData[key]?.active)
  }, [allDays, accumulatedScheduleData])

  useEffect(() => {
    if (!timeZone || !siteId) return
    if ((!day || !dayjs(day).isValid()) && viewDate) {
      navigate(PathUtils.getSchedule(siteId, viewDate), { replace: true })
    }
  }, [day, timeZone, siteId, viewDate, navigate])

  const onChangeLeaseDate = useCallback(
    (date: string): void => {
      if (!timeZone || !siteId) return

      const formattedDate = preserveDateInTimezone(date, timeZone).format(
        DATE_FORMAT.PATIENT_TRACKER_DATE,
      )

      if (day !== formattedDate) {
        navigate(PathUtils.getSchedule(siteId, formattedDate))
      }
    },
    [siteId, timeZone, day, navigate],
  )

  const handleBook = () => {
    navigate(PathUtils.getBook(siteId))
  }

  const handleChangeWeek = (date: Dayjs) => {
    if (!isDayView) {
      onChangeLeaseDate(date.format(DATE_FORMAT.PATIENT_TRACKER_DATE))
    }
  }

  const handleWarningPopup = () => {
    Popup.showInfo(
      {
        title: 'No Booking Found!',
        message: INFO_CONSTANTS.NO_ACTIVE_SCHEDULE_TODAY,
        mainButtonText: 'Book',
      },
      () => handleBook(),
    )
  }

  const doctorsIds = useMemo(() => doctorsUsers.map((staff) => String(staff.id)), [doctorsUsers])

  useEffect(() => {
    if (doctorsUsers.length > 0) {
      const hasDoctorInList = doctorsUsers.some(
        (user) => String(user.id) === selectedConsultationStaffId,
      )

      const storedValueExists =
        selectedConsultationStaffId &&
        (selectedConsultationStaffId === emptyOption.code || hasDoctorInList)

      if (!storedValueExists) {
        const findCurrentUser = doctorsUsers.find((item) => item.id === userId)
        const defaultConsultationStaffId = findCurrentUser?.id || emptyOption.code

        setSelectedConsultationStaffId(String(defaultConsultationStaffId))
      }
    }
  }, [doctorsUsers, userId, selectedConsultationStaffId, setSelectedConsultationStaffId])

  useEffect(() => {
    if (!isDayView || !scheduleRooms.length) return
    if (selectedRoomIds == null) {
      setSelectedRoomIds(scheduleRooms.map((room) => room.value))
    }
  }, [isDayView, scheduleRooms, selectedRoomIds, setSelectedRoomIds])

  const availableRoomValues = useMemo(
    () => roomFilterOptions.map((room) => room.value),
    [roomFilterOptions],
  )

  const checkedRoomIds = useMemo(() => {
    if (!availableRoomValues.length || selectedRoomIds == null) {
      return availableRoomValues
    }

    return selectedRoomIds.filter((roomId) => availableRoomValues.includes(roomId))
  }, [availableRoomValues, selectedRoomIds])

  const handleRoomsFilterChange = useCallback(
    (nextRoomIds: Array<string | number>) => {
      const nextCurrentDayRoomIds = nextRoomIds.map(String)
      const availableSet = new Set(availableRoomValues)
      const preservedRoomIds = (selectedRoomIds ?? []).filter((roomId) => !availableSet.has(roomId))

      setSelectedRoomIds([...preservedRoomIds, ...nextCurrentDayRoomIds])
    },
    [availableRoomValues, selectedRoomIds, setSelectedRoomIds],
  )

  const consultationStaffOptions = useMemo(() => {
    return [
      emptyOption,
      ...doctorsUsers.map((staff) => ({
        name: staff.name,
        code: String(staff.id),
      })),
    ]
  }, [doctorsUsers])

  useEffect(() => {
    if (doctorsIds.length) {
      setBookingIds(doctorsIds)
    }
  }, [doctorsIds, setBookingIds])

  useEffect(() => {
    return () => {
      dispatch(
        scheduleApi.util.invalidateTags([SCHEDULE_TAGS.SCHEDULE, SCHEDULE_TAGS.SCHEDULE_WEEK]),
      )
    }
  }, [dispatch])

  if (isPageLoading) {
    return (
      <Row className={styles.rowContainer}>
        <Col xs={24}>
          <ScheduleLoading />
        </Col>
      </Row>
    )
  }

  return (
    <Row className={styles.rowContainer}>
      <Col xs={24}>
        <Row
          id='schedule-header-calendar'
          justify={{ sm: 'start', xs: 'center' }}
          align={{ md: 'middle', xs: 'top' }}
          gutter={[12, 12]}>
          <Col xs={24}>
            <div className={styles.headerContainer}>
              <ScheduleHeaderCalendar
                onOpenChange={(open) => {
                  if (!open) {
                    setCalendarMonthDate(null)
                  }
                }}
                isFetching={isFetchingScheduleMonth}
                isLoading={false}
                defaultActiveDate={viewDate}
                availableDays={availableDays}
                scheduleData={accumulatedScheduleData}
                isWeekView={!isDayView}
                onDateChange={(date) => {
                  onChangeLeaseDate(date)
                }}
                handleClickDisabledDate={handleWarningPopup}
                onChangeMonth={(date) => {
                  setCalendarMonthDate(date.format(DATE_FORMAT.PATIENT_TRACKER_DATE))
                }}
                onChangeWeek={handleChangeWeek}
                timeZone={timeZone}
                forceEnableFutureDate
                forceEnablePastDate
              />

              <div className={styles.calendarViewSwitchContainer}>
                <Button
                  modifier={isDayView ? BUTTON_MODIFIER.PRIMARY : BUTTON_MODIFIER.DEFAULT}
                  className={classNames(
                    styles.calendarViewSwitch,
                    styles['calendarViewSwitch--not-selected'],
                  )}
                  onClick={() => {
                    const currentDate = preserveDateInTimezone(dayjs(), timeZone).format(
                      DATE_FORMAT.PATIENT_TRACKER_DATE,
                    )
                    onChangeLeaseDate(currentDate)
                    setCurrentView(EScheduleView.DAY)
                  }}>
                  Today
                </Button>
                <Button
                  modifier={isDayView ? BUTTON_MODIFIER.PRIMARY : BUTTON_MODIFIER.DEFAULT}
                  className={classNames(
                    styles.calendarViewSwitch,
                    !isDayView && styles['calendarViewSwitch--not-selected'],
                  )}
                  onClick={() => setCurrentView(EScheduleView.DAY)}>
                  Day
                </Button>
                <Button
                  modifier={!isDayView ? BUTTON_MODIFIER.PRIMARY : BUTTON_MODIFIER.DEFAULT}
                  className={classNames(
                    styles.calendarViewSwitch,
                    isDayView && styles['calendarViewSwitch--not-selected'],
                  )}
                  onClick={() => {
                    setCurrentView(EScheduleView.WEEK)
                  }}>
                  Week
                </Button>
                <div className={styles.zoomControls}>
                  <Button className={styles.zoomButton} onClick={handleZoomOut}>
                    <Icon icon='ic:round-minus' width={18} height={18} />
                  </Button>
                  <Button className={styles.zoomButton} onClick={handleZoomIn}>
                    <Icon icon='ic:round-plus' width={18} height={18} />
                  </Button>
                </div>
                <Select
                  containerClassName={styles.staffSelect}
                  className={styles.staffSelect}
                  value={selectedConsultationStaffId}
                  onChange={(value) => setSelectedConsultationStaffId(value)}
                  listOptions={consultationStaffOptions}
                  placeholder='Select consultation staff'
                />
                {!isDayView && (
                  <Checkbox
                    checked={showCanceled ?? true}
                    onChange={(e) => setShowCanceled(e.target.checked)}
                    className={styles.showCanceledCheckbox}>
                    Show canceled
                  </Checkbox>
                )}
                {isDayView && hasEmrEnabled && (
                  <div className={styles.roomsSelect}>
                    <SelectCheckbox
                      selectValue={checkedRoomIds}
                      options={roomFilterOptions}
                      checkBoxLabelKey='label'
                      checkBoxValueKey='value'
                      placeholder='Select rooms'
                      showSelectAll
                      onChange={handleRoomsFilterChange}
                      disabled={!roomFilterOptions.length}
                    />
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </Col>

      <Show>
        <Show.When isVisible={currentView === EScheduleView.DAY}>
          <ScheduleDayView
            defaultActiveDate={viewDate}
            selectedConsultationStaffId={selectedConsultationStaffId}
            selectedRoomIds={hasEmrEnabled ? selectedRoomIds : null}
            scheduleView={currentView}
            scheduleMonthData={accumulatedScheduleData}
            zoomInterval={zoomInterval}
          />
        </Show.When>
        <Show.Else>
          <ScheduleWeekView
            defaultActiveDate={viewDate}
            selectedConsultationStaffId={selectedConsultationStaffId}
            showCanceled={showCanceled ?? true}
            scheduleView={currentView}
            scheduleMonthData={accumulatedScheduleData}
            zoomInterval={zoomInterval}
          />
        </Show.Else>
      </Show>
    </Row>
  )
}

export default SchedulePage
